import React from 'react'
import { logoDict, tooltipDict } from '../utils'
import ReactTooltip from 'react-tooltip'

function MetricBox({ metricsSummaryData, adPlatformOptions, adPlatformWatch, apiError }) {

  const apiErrorTooltipId = `api-error-tooltip-${Math.random().toString(36).substring(2, 9)}`;

  return (
    <>
      {adPlatformWatch !== 'summary' ? (
        <div>
          {apiError && (
            <div className="tw-mb-4 tw-flex tw-items-center tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-md tw-p-3">
              <span className="tw-text-red-600 tw-mr-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  data-tip
                  data-for={apiErrorTooltipId}
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                <ReactTooltip id={apiErrorTooltipId} place="top" effect="solid">
                  <div className="tw-p-2 tw-max-w-xs tw-text-sm">
                    There seems to be an issue retrieving your ads API data. Please reconnect your account.
                  </div>
                </ReactTooltip>
              </span>
              <span className="tw-text-red-700 tw-font-medium">Attention needed: Please reconnect your account</span>
            </div>
          )}

          <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-12 tw-gap-4">
            {metricsSummaryData?.map((metric, i) => {
              const { metricName, value } = metric
              return (
                <div key={i} className="tw-col-span-1 lg:tw-col-span-4">
                  <MetricBoxComponent metric={metric} adPlatformOptions={adPlatformOptions} />
                </div>
              )
            })}
          </div>
        </div>
      ) : (
        <div>
          {Object.keys(metricsSummaryData).map((channel, i) => (
            <>
              <div className="tw-flex tw-items-center tw-mb-4">
                <img src={logoDict[channel]?.path} alt="Logo" className={logoDict[channel]?.size} />
                {logoDict[channel].title ? (
                  <h6 className="tw-mb-0">
                    <b>{adPlatformOptions?.find(({ value }) => value === channel)?.label || 'Unmatched Orders'}</b>
                  </h6>
                ) : null}
                {apiError[channel] && channel !== 'unmatched' && (
                  <span className="tw-ml-2 tw-text-red-600" data-tip data-for={`${apiErrorTooltipId}-${channel}`}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="8" x2="12" y2="12"></line>
                      <line x1="12" y1="16" x2="12.01" y2="16"></line>
                    </svg>
                    <ReactTooltip id={`${apiErrorTooltipId}-${channel}`} place="top" effect="solid">
                      <div className="tw-p-2 tw-max-w-xs tw-text-sm">
                        There seems to be an issue retrieving your ads API data. Please reconnect your account.
                      </div>
                    </ReactTooltip>
                  </span>
                )}
              </div>
              <div className="tw-grid tw-grid-cols-2 lg:tw-grid-cols-12 tw-gap-4">
                {metricsSummaryData[channel]?.map((metric, i) => {
                  const { metricName, value } = metric
                  return (
                    <div key={i} className="tw-col-span-1 lg:tw-col-span-3">
                      <MetricBoxComponent metric={metric} adPlatformOptions={adPlatformOptions} />
                    </div>
                  )
                })}
              </div>
              {i < Object.keys(metricsSummaryData).length - 1 && <hr />}
            </>
          ))}
        </div>
      )}
    </>
  )
}

export default MetricBox

const MetricBoxComponent = ({ metric, adPlatformOptions }) => {
  const { title, value, channel } = metric

    // Create a unique ID for each metric box for targeting tooltips
    const tooltipId = `tooltip-${title}-${Math.random().toString(36).substring(2, 9)}`;

    const tooltipChannel = channel === 'unmatched' ? 'unmatched': 'matched'

    // Determine if there's a tooltip for this metric
    const tooltipContent = tooltipChannel && tooltipDict[tooltipChannel] ? tooltipDict[tooltipChannel][title] : `Information about ${title}`;

    const getTitleSizeClass = (titleText) => {
      if (!titleText) return 'tw-text-base';

      // Long titles
      if (titleText.length > 24) return 'tw-text-xs';

      // Medium-long titles
      if (titleText.length > 16) return 'tw-text-sm';

      // Default size for regular titles
      return 'tw-text-base';
    };

    const titleSizeClass = getTitleSizeClass(title);

  return (
    <div className="tw-rounded-md tw-border tw-border-slate-200 tw-px-3 tw-py-2">
      <div
        className="tw-mb-4 tw-flex tw-items-center"
        data-tip
        data-for={tooltipId}
      >
        <div className={titleSizeClass}>{title}</div>
        <span className="tw-ml-1 tw-text-gray-400 tw-cursor-help">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
        </span>
      </div>
      <div className="tw-text-2xl tw-text-blue-700">{value}</div>
      <ReactTooltip id={tooltipId} place="top" effect="solid" className="!tw-max-w-xs !tw-whitespace-normal">
        <div className="tw-p-2 tw-text-sm tw-max-w-xs">
          {tooltipContent}
        </div>
      </ReactTooltip>
    </div>
  )
}
